/**
 * pv2-shared — Shared palette, types, and utilities for PrimateVisionV2 components
 * Phase 5 (PRI-291): extracted from PrimateVisionV2.tsx so component files can import
 * without depending on the main page module.
 */

import type { NarrativeEntry } from '../../lib/narrative'

// ─────────────────────────────────────────────────────────────────────────────
// Canonical model display name — single source of truth (Matt 2026-06-06).
// Used by the home-screen Advanced menu, the result pill, the onboarding copy,
// and the admin Demo Library so they can never drift again. The inference
// server reports an internal checkpoint label (e.g. 'primate-chimp-v0.0');
// this is the public-facing brand name we surface to users/admins.
// ─────────────────────────────────────────────────────────────────────────────
export const MODEL_DISPLAY_NAME = 'Darwin-preview-1.3B'

// PRI-534: lifecycle status shown next to the model name. The dashboard
// prefers the live `status` from GET /v1/models when it has it; this is the
// static fallback so the badge never flickers/disappears when the catalog
// fetch fails. Bump alongside MODEL_DISPLAY_NAME on model upgrades.
export const MODEL_STATUS_FALLBACK = 'preview'

/** 'preview' → 'Preview' — display label for a model lifecycle status. */
export function modelStatusLabel(status?: string | null): string {
  const s = status || MODEL_STATUS_FALLBACK
  return s.charAt(0).toUpperCase() + s.slice(1)
}

// ─────────────────────────────────────────────────────────────────────────────
// Light-mode colour palette (forced - bypasses user theme setting)
// ─────────────────────────────────────────────────────────────────────────────
export const L = {
  bgPrimary:    '#F2F0EB',
  bgSecondary:  '#E8E5DE',
  bgSurface:    '#FAFAF7',
  bgCode:       '#E0DDD5',
  border:       '#D4D0C8',
  textPrimary:  '#1A1916',
  textMuted:    '#7A7669',
  signal:       '#1B4FE0',
  signalHover:  '#1340C8',
  iconStrip:    '#EDEBE4',
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared types
// ─────────────────────────────────────────────────────────────────────────────

/**
 * PRI-552: terminal usage block from the public API (GET /v1/analyses/{id} /
 * GET /v1/streams/{id} → usage). Terminal-only — null/absent while the
 * analysis/stream is in flight. metering_rule mirrors the resource's
 * top-level field; frames is the processed-frame count (present for both
 * rules — frame counting predates the billing cutover).
 */
export interface PillUsage {
  billed_seconds: number
  credit_balance_after: number | null
  metering_rule?: 'per_second' | 'per_frame'
  frames?: number | null
}

export interface ResultPill {
  id: string
  prompt: string
  answer?: 'YES' | 'NO'
  confidence: number
  resultText: string
  videoThumb: string | null
  streamHistory?: Array<{ frame_num: number; answer: string; confidence: number; elapsed_s?: number; term_confidences?: Record<string, number>; recording_elapsed_ms?: number }>
  liveData?: { answer?: string; confidence?: number; query_type?: string; search_term?: string; inference_ms?: number; session_fps?: number }
  resultPayload?: Record<string, unknown> | null
  narrative: NarrativeEntry[]
  searchTerms?: string[]
  videoUrl?: string | null
  sessionResultId?: string
  userVideoId?: string
  savedAt?: string
  query_type?: string
  recordingUrl?: string
  /** PRI-414: terminal stream error stamped onto the pill when the session
   *  errors. streamPillIdRef is nulled on error (for the next session), which
   *  used to drop streamSession.errorMessage from the pill — the user saw a
   *  black RE-RUN STREAM card with a generic fallback instead of the reason. */
  streamErrorMessage?: string | null
  /** PRI-552: terminal billing summary (metering_rule + frames). Null/absent
   *  until the terminal GET response lands — never shown mid-session. */
  usage?: PillUsage | null
}

export interface VideoSource {
  kind: 'file' | 'stream'
  name: string
  thumbUrl?: string
}

/** PRI-437 P8: credits-era balance bar — seconds of processing credit remaining. */
export interface QuotaBarData {
  balanceSeconds: number | null
  thresholdSeconds: number | null
  loading: boolean
  /** PRI-503: balance belongs to an anonymous free-trial grant (signed-out).
      Low/exhausted cues upsell signup instead of Add credits. */
  anonTrial?: boolean
}

/** PRI-488 F1: pre-Run cost preview (validate_only dry-run / client mirror). */
export interface CostPreviewData {
  loading: boolean
  assessable: boolean | null
  estimatedSeconds: number | null
  balanceSeconds: number | null
  balanceAfter: number | null
  insufficient: boolean
  rephraseHint: string | null
  source: 'server' | 'client'
}

// ─────────────────────────────────────────────────────────────────────────────
// Mobile viewfinder zone geometry — PRI-494
// Single source of truth for the fixed 3-zone shell on mobile.
//
// On 390×844 (iPhone 15/16): header=44px, camera=219px (56.25vw), results≈376px,
// input≈132px (auto, see below), tabbar=49px. Short screens (≤700px tall): camera caps at 30vh.
// ─────────────────────────────────────────────────────────────────────────────
export const MOBILE_ZONES = {
  header: 44,
  cameraAspect: 9 / 16,
  /** vh cap for short screens (height ≤ 700px). Used in mobileCameraHeightCss(). */
  cameraMaxVh: 30,
  shortScreenMaxHeight: 700,
  /**
   * FALLBACK reserve for the input zone (px), used by the results-frame bottom
   * calc until the live `--pv2-input-h` CSS var is published.
   *
   * PRI-494 bug fix (2026-07-29, Matt: "Input covers menu bar"): the input zone
   * is NO LONGER a fixed 68px box — real composer content is ~132px+ and the
   * hard 68px height (with alignItems:center) made it overflow symmetrically
   * and paint OVER the tab bar. The zone is now auto-height anchored above the
   * tab bar (growth goes upward), and MobileLayout publishes the measured
   * height via `--pv2-input-h` so the results frame reserves exactly enough.
   */
  input: 132,
  tabBar: 49,
} as const

/** Returns the CSS height expression for the mobile camera zone.
 * Always `min(56.25vw, ${cameraMaxVh}vh)`.
 *
 * On tall phones (844px tall): min(219px, 253px) = 219px  → vw wins (full 16:9).
 * On short phones (667px tall): min(219px, 200px) = 200px → vh wins (capped at 30vh).
 *
 * This single expression satisfies both the standard and short-screen rules
 * without any JS innerHeight check. */
export function mobileCameraHeightCss(): string {
  return `min(56.25vw, ${MOBILE_ZONES.cameraMaxVh}vh)`
}

// ─────────────────────────────────────────────────────────────────────────────
// Utility helpers
// ─────────────────────────────────────────────────────────────────────────────

export function fmtMins(m: number): string {
  const h = Math.floor(m / 60)
  const min = Math.round(m % 60)
  if (h === 0) return `${min}m`
  return `${h}h ${min}m`
}

export function fmtTimeUntil(d: Date): string {
  const diff = d.getTime() - Date.now()
  if (diff <= 0) return 'now'
  const m = Math.round(diff / 60_000)
  if (m < 60) return `${m}m`
  const h = Math.floor(m / 60)
  const rem = m % 60
  return rem === 0 ? `${h}h` : `${h}h ${rem}m`
}

export function fmtTime(s: number): string {
  const m = Math.floor(s / 60)
  const sec = Math.floor(s % 60)
  return `${m}:${sec.toString().padStart(2, '0')}`
}

/** Codec probe: returns true if the browser can natively play the blob's media type. */
export function canPlayNatively(blob: Blob): boolean {
  const video = document.createElement('video')
  const type = blob.type || 'video/mp4'
  return video.canPlayType(type) === 'probably'
}

// ──────────────────────────────────────────────────────────────────────────
// stripCompiledQueryEnvelope — remove the wire-format compiled-query payload
// that embedCompiledQuery() appends to the prompt for transmission to the
// inference server. Used by every UI surface that renders a stored / replayed
// prompt so the user never sees the base64 envelope.
//
// Envelope format (matches embedCompiledQuery in src/primate/hooks/useSubmitHandlers.ts):
//   "<rawPrompt>|||COMPILED_QUERY:<base64-json>|||"
//
// The trailing "|||" closes the envelope but historic rows may be missing it.
// We strip from the first "|||COMPILED_QUERY:" onward unconditionally.
//
// This function is the canonical defense-in-depth boundary. Pill display, replay
// prompt extraction, copy-to-clipboard, downloads, and recorded-session reads
// all flow through here so a single envelope leak (e.g. a DB-stored wire prompt
// from before the useSubmitHandlers pill-prompt fix) can never render to the UI.
// ──────────────────────────────────────────────────────────────────────────
export const COMPILED_QUERY_DELIMITER = '|||COMPILED_QUERY:'

export function stripCompiledQueryEnvelope(prompt: string | null | undefined): string {
  if (!prompt) return ''
  const idx = prompt.indexOf(COMPILED_QUERY_DELIMITER)
  if (idx === -1) return prompt
  // Trim trailing whitespace + the wire-format suffix.
  return prompt.slice(0, idx).trim()
}
